<script lang="ts">
  import { untrack } from 'svelte';
  import { router } from '@inertiajs/svelte';
  import {
    isGlobalSearchOpen,
    openGlobalSearch,
    closeGlobalSearch,
    searchIncludeDone,
    searchIncludeArchived,
    searchIncludeBacklog,
  } from '../lib/stores/globalSearch';
  import { jsonHeaders } from '../lib/apiHelpers';
  import {
    fetchProjectTasks,
    getCachedTasks,
    invalidateProjectTasks,
    subscribeToAllCachedTasks,
    updateTaskInCache,
  } from '../lib/stores/projectTasksCache';
  import { completeTask, deleteTask as deleteTaskApi, markTaskWontDo } from '../lib/taskApi';
  import { applyTaskOptimisticUpdate } from '../lib/taskOptimistic';
  import { toast } from 'svelte-sonner';
  import type {
    Task,
    Project,
    ProjectCategory,
    ProjectSchedulingState,
    ProjectStatus,
  } from '../lib/types';
  import { categoryIcons } from '../lib/utils';
  import { formatProjectDateShort } from '../lib/projectDateRange';
  import HighlightedText from './HighlightedText.svelte';
  import SearchTypeIcon from './SearchTypeIcon.svelte';
  import TaskDetailModal from './TaskDetailModal.svelte';
  import { useBodyScrollLock } from '../lib/useBodyScrollLock.svelte';
  import { patchNowTask } from '../lib/stores/now';

  interface ProjectSearchResult {
    type: 'project';
    id: string;
    title: string;
    prefix?: string | null;
    category: ProjectCategory;
    status: ProjectStatus;
    schedulingState: ProjectSchedulingState;
    startDate?: string | null;
    endDate?: string | null;
    isArchived?: boolean;
    tags?: string[];
    updatedAt: string;
    url: string;
  }

  interface TaskSearchResult extends Task {
    type: 'task';
    projectTitle: string;
    projectUrl: string;
    projectStatus: ProjectStatus;
    projectSchedulingState: ProjectSchedulingState;
    projectArchived?: boolean;
  }

  type SearchResult = ProjectSearchResult | TaskSearchResult;

  const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
    planned: 'Planned',
    active: 'Active',
    almost: 'Almost',
    done: 'Done',
  };

  const TASK_STATUS_LABELS: Record<Task['status'], string> = {
    todo: 'Todo',
    done: 'Done',
    wont_do: "Won't do",
  };

  const PRIORITY_LABELS: Record<Task['priority'], string | null> = {
    none: null,
    low: 'Low',
    medium: 'Medium',
    high: 'High',
  };

  let query = $state('');
  let projects = $state<ProjectSearchResult[]>([]);
  let tasks = $state<TaskSearchResult[]>([]);
  let queryTokens = $state<string[]>([]);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let activeIndex = $state(0);
  let inputEl: HTMLInputElement | undefined = $state();
  let selectedTask = $state<TaskSearchResult | null>(null);
  let abortController: AbortController | null = null;
  // Search-result snapshots for rows removed by delete, so Undo can put them back.
  let removedSearchTasks: TaskSearchResult[] = [];
  // Only revive a deleted row when a fetched cache goes missing → present (Undo),
  // not when a cold fetch first publishes the still-living task.
  let seenAbsentInFetchedCache = new Set<string>();

  let open = $derived($isGlobalSearchOpen);
  let includeDone = $derived($searchIncludeDone);
  let includeArchived = $derived($searchIncludeArchived);
  let includeBacklog = $derived($searchIncludeBacklog);
  let results = $derived<SearchResult[]>([...projects, ...tasks]);

  useBodyScrollLock(() => open);
  let hasQuery = $derived(query.trim().length > 0);

  function resetSearchState() {
    abortController?.abort();
    query = '';
    projects = [];
    tasks = [];
    queryTokens = [];
    loading = false;
    error = null;
    activeIndex = 0;
    removedSearchTasks = [];
    seenAbsentInFetchedCache = new Set();
  }

  function mergeSearchTask(result: TaskSearchResult, cached: Task): TaskSearchResult {
    return {
      ...result,
      ...cached,
      type: 'task',
      projectTitle: result.projectTitle,
      projectUrl: result.projectUrl,
      projectStatus: result.projectStatus,
      projectSchedulingState: result.projectSchedulingState,
      projectArchived: result.projectArchived,
    };
  }

  function searchTaskChanged(a: TaskSearchResult, b: TaskSearchResult): boolean {
    return (
      a.status !== b.status ||
      a.completedAt !== b.completedAt ||
      a.isActive !== b.isActive ||
      a.title !== b.title ||
      a.notes !== b.notes ||
      a.priority !== b.priority
    );
  }

  function isRemovedSearchTask(taskId: string): boolean {
    return removedSearchTasks.some((entry) => entry.id === taskId);
  }

  function overlaySearchTasksFromCache(searchTasks: TaskSearchResult[]): TaskSearchResult[] {
    return searchTasks.flatMap((result) => {
      const cached = getCachedTasks(result.projectId);
      if (cached.fetched) {
        const match = cached.tasks.find((task) => task.id === result.id);
        if (!match) return [];
        return [mergeSearchTask(result, match)];
      }
      if (isRemovedSearchTask(result.id)) return [];
      return [result];
    });
  }

  function applyCachedTasksToSearch(all: Record<string, Task[]>) {
    if (!hasQuery) return;

    let next = tasks.map((result) => {
      const cachedList = all[result.projectId];
      if (!cachedList) return result;
      const cached = cachedList.find((task) => task.id === result.id);
      if (!cached) return result;
      const merged = mergeSearchTask(result, cached);
      return searchTaskChanged(result, merged) ? merged : result;
    });

    next = next.filter((result) => {
      const cachedList = all[result.projectId];
      if (!cachedList) return true;
      return cachedList.some((task) => task.id === result.id);
    });

    for (const hidden of removedSearchTasks) {
      const cachedList = all[hidden.projectId];
      if (!cachedList) continue;
      const cached = cachedList.find((task) => task.id === hidden.id);
      if (!cached) {
        seenAbsentInFetchedCache.add(hidden.id);
        continue;
      }
      if (
        seenAbsentInFetchedCache.has(hidden.id) &&
        !next.some((result) => result.id === hidden.id)
      ) {
        next = [mergeSearchTask(hidden, cached), ...next];
        seenAbsentInFetchedCache.delete(hidden.id);
        removedSearchTasks = removedSearchTasks.filter((entry) => entry.id !== hidden.id);
      }
    }

    if (next.length !== tasks.length || next.some((result, index) => result !== tasks[index])) {
      tasks = next;
    }

    if (!selectedTask) return;
    const cachedList = all[selectedTask.projectId];
    if (!cachedList) return;
    const cached = cachedList.find((task) => task.id === selectedTask!.id);
    if (!cached) {
      selectedTask = null;
      return;
    }
    const merged = mergeSearchTask(selectedTask, cached);
    if (searchTaskChanged(selectedTask, merged)) selectedTask = merged;
  }

  async function tasksForMutation(task: Task): Promise<Task[] | null> {
    const cached = getCachedTasks(task.projectId);
    if (cached.fetched) {
      if (cached.tasks.some((entry) => entry.id === task.id)) return cached.tasks;
      return [...cached.tasks, task];
    }

    const list = await fetchProjectTasks(task.projectId);
    if (list.some((entry) => entry.id === task.id)) return list;

    // Cold miss: do not publish a one-task cache via taskApi. Drop an empty
    // fetched entry so a failed project fetch cannot zero-out board/index counts.
    const after = getCachedTasks(task.projectId);
    if (after.fetched && after.tasks.length === 0) {
      invalidateProjectTasks(task.projectId);
    }
    toast.error('Failed to update task');
    return null;
  }

  function rememberRemovedSearchTask(task: TaskSearchResult) {
    if (!removedSearchTasks.some((entry) => entry.id === task.id)) {
      removedSearchTasks = [...removedSearchTasks, task];
    }
  }

  function isTextEntryTarget(target: EventTarget | null): boolean {
    return (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      (target instanceof HTMLElement && target.isContentEditable)
    );
  }

  // eslint-disable-next-line momentium/no-unguarded-effect -- global keyboard shortcut listener lifecycle for command palette
  $effect(() => {
    function handleGlobalKeydown(e: KeyboardEvent) {
      if (e.defaultPrevented || open || isTextEntryTarget(e.target)) return;
      if (!e.metaKey && !e.ctrlKey && !e.altKey && ['k', '/'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        openGlobalSearch();
      }
    }

    window.addEventListener('keydown', handleGlobalKeydown);
    return () => window.removeEventListener('keydown', handleGlobalKeydown);
  });

  // eslint-disable-next-line momentium/no-unguarded-effect -- modal focus/reset lifecycle when search opens
  $effect(() => {
    if (open) {
      resetSearchState();
      setTimeout(() => inputEl?.focus(), 0);
    } else {
      selectedTask = null;
    }
  });

  // eslint-disable-next-line momentium/no-unguarded-effect -- debounced remote search request based on query text and include flags
  $effect(() => {
    const q = query.trim();
    const done = includeDone;
    const archived = includeArchived;
    const backlog = includeBacklog;
    activeIndex = 0;

    if (!q) {
      projects = [];
      tasks = [];
      removedSearchTasks = [];
      seenAbsentInFetchedCache = new Set();
      loading = false;
      error = null;
      abortController?.abort();
      return;
    }

    loading = true;
    error = null;
    const timeout = setTimeout(() => search(q, done, archived, backlog), 180);

    return () => clearTimeout(timeout);
  });

  // eslint-disable-next-line momentium/no-unguarded-effect -- external subscription: projectTasksCache pub/sub so search rows follow complete/won't-do/delete undo
  $effect(() => {
    return subscribeToAllCachedTasks((all) => {
      untrack(() => applyCachedTasksToSearch(all));
    });
  });

  async function search(q: string, done: boolean, archived: boolean, backlog: boolean) {
    abortController?.abort();
    abortController = new AbortController();

    const params = new URLSearchParams({ q });
    if (done) params.set('include_done', 'true');
    if (archived) params.set('include_archived', 'true');
    if (!backlog) params.set('include_backlog', 'false');

    try {
      const response = await fetch(`/search.json?${params.toString()}`, {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        signal: abortController.signal,
        // Search results are dynamic, per-user data — never let the browser
        // serve or revalidate a cached copy. Rails' default conditional GET
        // (ETag + must-revalidate) can return 304, which Safari surfaces to
        // fetch() as an empty-body non-ok response.
        cache: 'no-store',
      });

      // 304 Not Modified: the server matched a cached ETag. With cache:
      // 'no-store' this shouldn't occur, but if it does, keep current results
      // rather than failing.
      if (response.status === 304) return;
      if (!response.ok) throw new Error('Search failed');

      const json = await response.json();
      projects = json.projects || [];
      tasks = overlaySearchTasksFromCache(json.tasks || []);
      queryTokens = json.queryTokens || [];
      error = null;
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        error = 'Search failed. Please try again.';
        projects = [];
        tasks = [];
      }
    } finally {
      loading = false;
    }
  }

  function resetAndClose() {
    closeGlobalSearch();
  }

  function navigateToProject(result: ProjectSearchResult) {
    resetAndClose();
    router.get(result.url);
  }

  function navigateToTaskProject(url?: string) {
    if (!url) return;
    selectedTask = null;
    resetAndClose();
    router.get(url);
  }

  function openTask(result: TaskSearchResult) {
    selectedTask = result;
  }

  function selectResult(result: SearchResult) {
    if (result.type === 'project') navigateToProject(result);
    else openTask(result);
  }

  function handleKeydown(e: KeyboardEvent) {
    if (selectedTask) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      resetAndClose();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      activeIndex = results.length ? (activeIndex + 1) % results.length : 0;
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      activeIndex = results.length ? (activeIndex - 1 + results.length) % results.length : 0;
      return;
    }

    if (e.key === 'Enter' && results[activeIndex]) {
      if (e.target instanceof HTMLButtonElement && !e.target.classList.contains('result-item')) {
        return;
      }
      e.preventDefault();
      selectResult(results[activeIndex]);
    }
  }

  function resultIndex(type: 'project' | 'task', index: number) {
    return type === 'project' ? index : projects.length + index;
  }

  function toggleIncludeDone() {
    searchIncludeDone.update((value) => !value);
  }

  function toggleIncludeArchived() {
    searchIncludeArchived.update((value) => !value);
  }

  function toggleIncludeBacklog() {
    searchIncludeBacklog.update((value) => !value);
  }

  function projectBadge(project: ProjectSearchResult): { label: string; kind: string } {
    if (project.isArchived) return { label: 'Archived', kind: 'archived' };
    if (project.schedulingState === 'backlog') return { label: 'Backlog', kind: 'backlog' };
    return { label: PROJECT_STATUS_LABELS[project.status], kind: project.status };
  }

  function projectMetaParts(project: ProjectSearchResult): string[] {
    const categoryLabel = project.category.charAt(0).toUpperCase() + project.category.slice(1);
    const category = `${categoryIcons[project.category] ?? ''} ${categoryLabel}`.trim();
    if (project.schedulingState === 'backlog') return [category];
    const schedule =
      project.startDate && project.endDate
        ? formatProjectDateShort({
            startDate: project.startDate,
            endDate: project.endDate,
          } as Project)
        : 'Scheduled';
    return [category, schedule];
  }

  function taskParentNote(task: TaskSearchResult): string | null {
    if (task.projectArchived) return 'Archived project';
    if (task.projectStatus === 'done') return 'Done project';
    return null;
  }

  function replaceTask(
    updated: TaskSearchResult,
    updateSelected = selectedTask?.id === updated.id,
  ) {
    tasks = tasks.map((t) => (t.id === updated.id ? updated : t));
    if (updateSelected) selectedTask = updated;
  }

  async function updateTask(task: Task, updates: Partial<Task>) {
    const current = selectedTask;
    if (!current) return;
    const optimistic = applyTaskOptimisticUpdate(current, updates) as TaskSearchResult;
    replaceTask(optimistic);
    updateTaskInCache(task.projectId, task.id, updates);
    patchNowTask(task.id, updates);

    try {
      const response = await fetch(`/projects/${task.projectId}/tasks/${task.id}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ task: updates }),
      });
      if (!response.ok) throw new Error('Update failed');
      const json = await response.json();
      updateTaskInCache(task.projectId, task.id, json);
      replaceTask({ ...current, ...json }, selectedTask?.id === task.id);
    } catch {
      updateTaskInCache(task.projectId, task.id, current);
      replaceTask(current, selectedTask?.id === task.id);
    }
  }

  async function deleteTask(task: Task, pendingUpdates?: Partial<Task>) {
    if (pendingUpdates) await updateTask(task, pendingUpdates);
    const currentTasks = await tasksForMutation(task);
    if (!currentTasks) return;
    const current =
      selectedTask?.id === task.id
        ? selectedTask
        : (tasks.find((entry) => entry.id === task.id) ?? null);
    if (current) rememberRemovedSearchTask(current);
    selectedTask = null;
    tasks = tasks.filter((entry) => entry.id !== task.id);
    await deleteTaskApi(task.projectId, task.id, currentTasks);
  }

  async function markWontDo(task: Task, pendingUpdates?: Partial<Task>) {
    const currentTasks = await tasksForMutation(task);
    if (!currentTasks) return;
    await markTaskWontDo(task.projectId, task.id, currentTasks, pendingUpdates);
  }

  async function toggleComplete(task: Task, pendingUpdates?: Partial<Task>) {
    const currentTasks = await tasksForMutation(task);
    if (!currentTasks) return;
    await completeTask(task.projectId, task.id, currentTasks, pendingUpdates);
  }
</script>

<svelte:window onkeydown={open ? handleKeydown : undefined} />

{#if open}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="search-backdrop" onmousedown={resetAndClose} role="presentation">
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="search-modal"
      role="dialog"
      tabindex="-1"
      aria-modal="true"
      aria-label="Global project and task search"
      onmousedown={(e) => e.stopPropagation()}
    >
      <div class="search-input-row">
        <span class="search-icon">⌕</span>
        <input
          bind:this={inputEl}
          bind:value={query}
          class="search-input"
          type="text"
          placeholder="Search projects and tasks..."
          autocomplete="off"
        />
        <span class="shortcut-hint kbd-hint">Esc</span>
      </div>

      <div class="search-filters">
        <span class="filter-label">Include</span>
        <button
          type="button"
          class="filter-pill"
          aria-pressed={includeBacklog}
          onmousedown={(e) => e.preventDefault()}
          onclick={toggleIncludeBacklog}
        >
          Backlog
        </button>
        <button
          type="button"
          class="filter-pill"
          aria-pressed={includeDone}
          onmousedown={(e) => e.preventDefault()}
          onclick={toggleIncludeDone}
        >
          Done projects
        </button>
        <button
          type="button"
          class="filter-pill"
          aria-pressed={includeArchived}
          onmousedown={(e) => e.preventDefault()}
          onclick={toggleIncludeArchived}
        >
          Archived
        </button>
      </div>

      <div class="search-results">
        {#if error}
          <div class="empty-state error">{error}</div>
        {:else if !hasQuery}
          <div class="empty-state">
            Start typing to search projects and tasks.
            <span class="kbd-hint">Use <kbd>↑</kbd><kbd>↓</kbd> and <kbd>Enter</kbd>.</span>
          </div>
        {:else if loading && results.length === 0}
          <div class="empty-state">Searching…</div>
        {:else if results.length === 0}
          <div class="empty-state">
            <div>No results for “{query.trim()}”.</div>
            {#if !includeDone || !includeArchived || !includeBacklog}
              <div class="empty-hint">Try including backlog, done, or archived projects.</div>
            {/if}
          </div>
        {:else}
          {#if projects.length > 0}
            <div class="result-section-label">Projects</div>
            {#each projects as project, i (project.id)}
              {@const idx = resultIndex('project', i)}
              {@const badge = projectBadge(project)}
              <button
                type="button"
                class="result-item"
                class:active={activeIndex === idx}
                onmouseenter={() => (activeIndex = idx)}
                onclick={() => navigateToProject(project)}
              >
                <span class="result-icon">
                  <SearchTypeIcon kind="project" />
                </span>
                <span class="result-content">
                  <span class="result-title-row">
                    <HighlightedText variant="title" text={project.title} tokens={queryTokens} />
                    <span
                      class="badge"
                      class:badge-active={badge.kind === 'active'}
                      class:badge-planned={badge.kind === 'planned'}
                      class:badge-almost={badge.kind === 'almost'}
                      class:badge-done={badge.kind === 'done'}
                      class:badge-backlog={badge.kind === 'backlog'}
                      class:badge-archived={badge.kind === 'archived'}>{badge.label}</span
                    >
                  </span>
                  <span class="result-meta">{projectMetaParts(project).join(' · ')}</span>
                  {#if project.tags?.length}
                    <span class="result-tags">
                      {#each project.tags as tag (tag)}
                        <span class="result-tag">{tag}</span>
                      {/each}
                    </span>
                  {/if}
                </span>
              </button>
            {/each}
          {/if}

          {#if tasks.length > 0}
            <div class="result-section-label">Tasks</div>
            {#each tasks as task, i (task.id)}
              {@const idx = resultIndex('task', i)}
              {@const priorityLabel = PRIORITY_LABELS[task.priority]}
              {@const parentNote = taskParentNote(task)}
              <button
                type="button"
                class="result-item"
                class:active={activeIndex === idx}
                onmouseenter={() => (activeIndex = idx)}
                onclick={() => openTask(task)}
              >
                <span class="result-icon">
                  <SearchTypeIcon kind="task" />
                </span>
                <span class="result-content">
                  <span
                    class="result-title-row"
                    class:completed={task.status === 'done' || task.status === 'wont_do'}
                  >
                    <HighlightedText variant="title" text={task.title} tokens={queryTokens} />
                    <span
                      class="badge"
                      class:badge-todo={task.status === 'todo'}
                      class:badge-done={task.status === 'done'}
                      class:badge-wont_do={task.status === 'wont_do'}
                      >{TASK_STATUS_LABELS[task.status]}</span
                    >
                  </span>
                  <span class="result-meta">
                    {#if priorityLabel}
                      <span
                        class="badge badge-sm"
                        class:badge-priority-low={task.priority === 'low'}
                        class:badge-priority-medium={task.priority === 'medium'}
                        class:badge-priority-high={task.priority === 'high'}>{priorityLabel}</span
                      >
                    {/if}
                    <span class="sr-only">in </span>
                    <span class="meta-into">
                      <SearchTypeIcon kind="into" />
                    </span>
                    <HighlightedText variant="meta" text={task.projectTitle} tokens={queryTokens} />
                    {#if parentNote}
                      <span class="meta-note">{parentNote}</span>
                    {/if}
                  </span>
                </span>
              </button>
            {/each}
          {/if}
        {/if}
      </div>
    </div>
  </div>
{/if}

<TaskDetailModal
  task={selectedTask}
  projectTitle={selectedTask?.projectTitle}
  projectUrl={selectedTask?.projectUrl}
  onClose={() => (selectedTask = null)}
  onUpdate={updateTask}
  onDelete={deleteTask}
  onMarkWontDo={markWontDo}
  onToggleComplete={toggleComplete}
  onProjectNavigate={navigateToTaskProject}
/>

<style>
  .search-backdrop {
    position: fixed;
    inset: 0;
    z-index: 250;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 12vh 1rem 1rem;
    background: rgba(17, 24, 39, 0.36);
  }

  .search-modal {
    width: min(640px, 100%);
    max-height: min(680px, calc(100vh - 2rem));
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.75rem;
    box-shadow:
      0 24px 64px rgba(0, 0, 0, 0.24),
      0 8px 18px rgba(0, 0, 0, 0.12);
  }

  .search-input-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.875rem 1rem 0.625rem;
  }

  .search-icon {
    color: #6b7280;
    font-size: 1.25rem;
  }

  .search-input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    font: inherit;
    font-size: 1rem;
    color: #111827;
  }

  .search-input::placeholder {
    color: #9ca3af;
  }

  .search-filters {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0 1rem 0.625rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .filter-label {
    margin-right: 0.125rem;
    font-size: 0.6875rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .filter-pill {
    padding: 0.15rem 0.5rem;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    background: #f9fafb;
    color: #4b5563;
    font: inherit;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
  }

  .filter-pill[aria-pressed='true'] {
    background: #eff6ff;
    color: #1d4ed8;
    border-color: #bfdbfe;
  }

  .shortcut-hint,
  kbd {
    padding: 0.125rem 0.35rem;
    font-size: 0.6875rem;
    font-weight: 600;
    color: #6b7280;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
  }

  .search-results {
    overflow-y: auto;
    padding: 0.5rem;
  }

  .result-section-label {
    padding: 0.5rem 0.5rem 0.25rem;
    font-size: 0.6875rem;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .result-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    width: 100%;
    padding: 0.625rem 0.75rem;
    border: none;
    border-radius: 0.5rem;
    background: transparent;
    text-align: left;
    cursor: pointer;
    font: inherit;
  }

  .result-item:hover,
  .result-item.active {
    background: #eff6ff;
  }

  .result-icon {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
    margin-top: 0.15rem;
    color: #6b7280;
  }

  .result-item:hover .result-icon,
  .result-item.active .result-icon {
    color: #2563eb;
  }

  .result-content {
    min-width: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .result-title-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }

  .result-title-row :global(.title) {
    flex: 1;
    min-width: 0;
  }

  .result-title-row.completed :global(.title) {
    color: #6b7280;
    text-decoration: line-through;
  }

  .result-meta {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    min-width: 0;
    color: #6b7280;
    font-size: 0.75rem;
  }

  .result-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    margin-top: 0.2rem;
  }

  .result-tag {
    padding: 0.05rem 0.4rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #1d4ed8;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 9999px;
  }

  .result-meta :global(.meta) {
    flex: 0 1 auto;
    min-width: 0;
  }

  .meta-into {
    flex-shrink: 0;
    display: flex;
    color: #9ca3af;
  }

  .meta-note {
    flex-shrink: 0;
    color: #9ca3af;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .badge {
    flex-shrink: 0;
    padding: 0.1rem 0.4rem;
    border-radius: 999px;
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    line-height: 1.3;
  }

  .badge-sm {
    padding: 0.05rem 0.3rem;
    font-size: 0.5625rem;
  }

  .badge-planned,
  .badge-todo {
    color: #1d4ed8;
    background: #dbeafe;
  }

  .badge-backlog {
    color: #7c5340;
    background: #f0e4d4;
  }

  .badge-active,
  .badge-almost {
    color: #b45309;
    background: #fef3c7;
  }

  .badge-done {
    color: #15803d;
    background: #dcfce7;
  }

  .badge-archived,
  .badge-wont_do {
    color: #4b5563;
    background: #f3f4f6;
    box-shadow: inset 0 0 0 1px #e5e7eb;
  }

  .badge-priority-low {
    color: #1d4ed8;
    background: #dbeafe;
  }

  .badge-priority-medium {
    color: #b45309;
    background: #fef3c7;
  }

  .badge-priority-high {
    color: #b91c1c;
    background: #fee2e2;
  }

  .empty-state {
    padding: 2.5rem 1rem;
    text-align: center;
    color: #6b7280;
    font-size: 0.875rem;
  }

  .empty-hint {
    margin-top: 0.35rem;
    font-size: 0.75rem;
    color: #9ca3af;
  }

  .empty-state.error {
    color: #dc2626;
  }

  @media (max-width: 640px) {
    .search-backdrop {
      align-items: flex-start;
      padding-top: 4rem;
    }
  }
</style>
